import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { UserPlus } from "lucide-react";
import api from "../lib/api";
import ProjectCard from "../components/ProjectCard";
import { useAuth } from "../lib/auth";

export default function ClientSelector() {
  const { user } = useAuth();
  const [projects, setProjects] = useState(null);
  const [archived, setArchived] = useState([]);

  useEffect(() => {
    (async () => {
      const [{ data: active }, { data: arch }] = await Promise.all([
        api.get("/projects", { params: { archived: false } }),
        api.get("/projects", { params: { archived: true } }),
      ]);
      setProjects(active);
      setArchived(arch);
    })();
  }, []);

  const isClientAdmin = user && user.role === "client" && (user.permission === "edit" || user.client_role === "admin");

  if (projects === null) return <div className="max-w-6xl mx-auto px-6 py-16 label">Loading…</div>;

  // If exactly one project in total, jump straight in
  if (projects.length === 1 && archived.length === 0) {
    return <Navigate to={`/portal/projects/${projects[0].id}`} replace />;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 fade-up" data-testid="client-selector">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-2">Welcome back</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-white">
            Your projects
          </h1>
          {user?.name && (
            <div className="mt-1 text-sm text-[color:var(--atech-text-dim)]">
              Welcome back, {user.name.split(" ")[0]}.
            </div>
          )}
          <p className="mt-3 text-sm text-[color:var(--atech-text-dim)] max-w-xl">
            Select a project to see status, drawings, documents and uploads shared by A-Tech.
          </p>
        </div>
        {isClientAdmin && (
          <Link
            to="/portal/team?invite=1"
            className="flex items-center gap-2 text-sm px-4 py-2.5 rounded-[2px] border border-[color:var(--atech-copper)] text-[color:var(--atech-copper)] hover:bg-[rgba(198,132,88,0.08)] transition-colors shrink-0"
            data-testid="dashboard-invite-teammate"
          >
            <UserPlus size={14} /> Invite teammate
          </Link>
        )}
      </div>

      <section className="mt-14">
        <div className="label mb-5">Active projects</div>
        {projects.length === 0 ? (
          <div className="card-surface p-10 text-sm text-[color:var(--atech-text-muted)]">
            You don't have any active projects yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} to={`/portal/projects/${p.id}`} />
            ))}
          </div>
        )}
      </section>

      {archived.length > 0 && (
        <section className="mt-16">
          <div className="label mb-5">Historic / archived</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 opacity-85">
            {archived.map((p) => (
              <ProjectCard key={p.id} project={p} to={`/portal/projects/${p.id}`} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
